/**
 * Link an NCR to the physical pieces it concerns. CR-2026-QDP (2026-10-14). Server: /api/qdp-links/ncr/:register/:id/pieces.
 * Replaces the typed part number for the quality data package. A wrong link is voided with a reason.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission } from "../../utils/hasPermission";

const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

export default function NcrPiecesPanel({ ncrId, jobId, register = "ncr-reports" }) {
  const { loginUser } = useSelector((s) => s.userSlice);
  const canEdit = hasPermission(loginUser?.decodedToken?.permissions || [], "edit_ncr");
  const [linked, setLinked] = useState([]);
  const [marks, setMarks] = useState([]);
  const [itemId, setItemId] = useState("");
  const [pieces, setPieces] = useState([]);
  const [sel, setSel] = useState([]);
  const [voidReason, setVoidReason] = useState("");
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    if (!ncrId) return;
    try { const r = await ApiCall.get(`/qdp-links/ncr/${register}/${ncrId}/pieces`); setLinked(r.data.data.pieces || []); } catch (_) { setLinked([]); }
  }, [ncrId, register]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setMarks([]);
    if (!jobId) return;
    ApiCall.get(`/fabricated-items/job/${jobId}`).then((r) => setMarks(Array.isArray(r.data?.data) ? r.data.data : [])).catch(() => setMarks([]));
  }, [jobId]);
  useEffect(() => {
    setPieces([]); setSel([]);
    if (!itemId) return;
    ApiCall.get(`/piece-trace/items/${itemId}/pieces`).then((r) => setPieces((r.data.data || []).filter((p) => p.status === "ACTIVE"))).catch(() => setPieces([]));
  }, [itemId]);
  const add = async () => {
    try { const r = await ApiCall.post(`/qdp-links/ncr/${register}/${ncrId}/pieces`, { pieceIds: sel }); setMsg({ severity: "success", text: r.data.message }); setSel([]); load(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not linked.") }); }
  };
  const voidIt = async (id) => {
    try { const r = await ApiCall.post(`/qdp-links/ncr-pieces/${id}/void`, { reason: voidReason }); setMsg({ severity: "success", text: r.data.message }); setVoidReason(""); load(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not voided.") }); }
  };
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="subtitle2">Pieces this NCR concerns</Typography>
      {!jobId && <Typography variant="caption" color="error">Record the job on the NCR first.</Typography>}
      {msg && <Alert severity={msg.severity} sx={{ my: 1 }}>{msg.text}</Alert>}
      {linked.length === 0 ? <Typography variant="caption" display="block">None linked. The quality data package will match this NCR only by its typed part or heat number, and will say so.</Typography>
        : linked.map((l) => (
          <Stack key={l.id} direction="row" spacing={1} alignItems="center" sx={{ opacity: l.voidedAt ? 0.5 : 1 }}>
            <Typography variant="body2">{l.display} <code>{l.publicId}</code> {l.voidedAt ? `(voided: ${l.voidReason})` : ""}</Typography>
            {canEdit && !l.voidedAt && <Button size="small" color="error" disabled={voidReason.trim().length < 4} onClick={() => voidIt(l.id)}>Void</Button>}
          </Stack>
        ))}
      {canEdit && jobId && (
        <>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField select size="small" label="Mark" value={itemId} onChange={(e) => setItemId(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="">Choose a mark</MenuItem>
              {marks.map((m) => <MenuItem key={m.id} value={m.id}>{m.name} (qty {m.quantity})</MenuItem>)}
            </TextField>
            <Button size="small" variant="contained" disabled={!sel.length} onClick={add}>Link {sel.length} piece(s)</Button>
            <TextField size="small" label="Reason to void a link" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
          </Stack>
          <Box>{pieces.map((p) => (
            <FormControlLabel key={p.id} label={`${p.mark} ${p.display}`}
              control={<Checkbox size="small" checked={sel.includes(p.id)} onChange={() => setSel((s) => (s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id]))} />} />
          ))}</Box>
        </>
      )}
    </Box>
  );
}
